import React from "react";
import { NavLink, Outlet, useNavigate, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { roleLabel } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import BrandLogo from "@/components/BrandLogo";
import {
  LayoutDashboard, Users, UserPlus, ClipboardList, Wrench, CalendarClock,
  LogOut, PhoneCall, Settings, LineChart, Layers, Brain, KeyRound,
  Scroll, Mail,
} from "lucide-react";

const NavItem = ({ to, icon: Icon, label, testId }) => (
  <NavLink
    to={to}
    end
    data-testid={testId}
    className={({ isActive }) =>
      "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors duration-200 " +
      (isActive
        ? "bg-primary text-primary-foreground"
        : "text-foreground/70 hover:bg-secondary hover:text-foreground")
    }
  >
    <Icon className="h-4 w-4" />
    <span>{label}</span>
  </NavLink>
);

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  if (!user) {
    navigate("/login");
    return null;
  }

  // Force-password-change gate: if the user must change their password (fresh
  // admin-created account or recent reset), lock them to /app/account until
  // they've picked a new one. Server-side is the source of truth — this is
  // pure UX polish.
  if (user.must_change_password && location.pathname !== "/app/account") {
    return <Navigate to="/app/account" replace />;
  }

  const role = user.role;
  const showProspects = role !== "Execution";
  const showExecution = role === "Admin" || role === "Advisor" || role === "Execution";
  const showReviews = role !== "ServiceRep" && role !== "Execution";
  const showAdmin = role === "Admin";
  const showTools = role === "Admin" || role === "Advisor";

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 border-r border-border bg-white lg:flex lg:flex-col">
        <div className="flex items-center gap-3 border-b border-border px-5 py-5">
          <BrandLogo size="sm" />
          <div className="min-w-0">
            <div className="overline text-muted-foreground">Advisory OS</div>
          </div>
        </div>
        <nav className="flex-1 space-y-1 overflow-y-auto min-h-0 px-3 py-4">
          <NavItem to="/app" icon={LayoutDashboard} label="Dashboard" testId="nav-dashboard" />
          {showProspects && (
            <NavItem to="/app/prospects" icon={UserPlus} label="Prospects" testId="nav-prospects" />
          )}
          {(role === "Admin" || role === "Advisor") && (
            <NavItem to="/app/discovery-calls" icon={PhoneCall} label="Discovery Calls" testId="nav-discovery-calls" />
          )}
          <NavItem to="/app/clients" icon={Users} label="Clients" testId="nav-clients" />
          {showExecution && (
            <NavItem to="/app/execution" icon={Wrench} label="Execution Queue" testId="nav-execution" />
          )}
          {showReviews && (
            <NavItem to="/app/reviews" icon={CalendarClock} label="Reviews" testId="nav-reviews" />
          )}
          {showTools && (
            <NavItem to="/app/tools" icon={Layers} label="Tools" testId="nav-tools" />
          )}
          {showAdmin && (
            <NavItem to="/app/intelligence" icon={Brain} label="Intelligence" testId="nav-intelligence" />
          )}
          {showAdmin && (
            <NavItem to="/app/research/mutual-funds" icon={LineChart} label="Research (MF)" testId="nav-research" />
          )}
          {showAdmin && (
            <NavItem to="/app/research/benchmarks" icon={LineChart} label="Benchmarks" testId="nav-benchmarks" />
          )}
          {showAdmin && (
            <NavItem to="/app/settings/availability" icon={Settings} label="Availability" testId="nav-availability" />
          )}
          {showAdmin && (
            <NavItem to="/app/team" icon={Users} label="Team Members" testId="nav-team" />
          )}
          {showAdmin && (
            <NavItem to="/app/email-templates" icon={Mail} label="Email Templates" testId="nav-email-templates" />
          )}
          {showAdmin && (
            <NavItem to="/app/audit-log" icon={Scroll} label="Audit Log" testId="nav-audit-log" />
          )}
        </nav>
        <div className="shrink-0 border-t border-border p-4">
          <div className="mb-3">
            <div className="text-sm font-semibold text-foreground" data-testid="current-user-name">{user.name}</div>
            <div className="overline text-muted-foreground" data-testid="current-user-role">{roleLabel(user.role)}</div>
          </div>
          <NavLink
            to="/app/account"
            className={({ isActive }) =>
              "mb-2 flex items-center gap-2 rounded-md px-3 py-1.5 text-xs font-medium transition-colors " +
              (isActive ? "bg-secondary text-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground")
            }
            data-testid="nav-account"
          >
            <KeyRound className="h-3.5 w-3.5" /> Change password
          </NavLink>
          <Button
            variant="outline"
            size="sm"
            className="w-full justify-start gap-2"
            onClick={logout}
            data-testid="logout-btn"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </Button>
        </div>
      </aside>

      {/* Mobile top bar (simplified) */}
      <div className="sticky top-0 z-20 flex items-center justify-between border-b border-border bg-white/80 px-4 py-3 backdrop-blur lg:hidden">
        <BrandLogo size="sm" />
        <Button variant="outline" size="sm" onClick={logout} data-testid="logout-btn-mobile">
          <LogOut className="h-4 w-4" />
        </Button>
      </div>

      {/* Main */}
      <main className="lg:pl-64">
        <div className="mx-auto max-w-7xl px-4 py-6 md:px-8 md:py-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
